import { useCallback, useEffect, useRef } from "react";

interface Options {
  thresholdMs?: number;
  moveTolerance?: number;
}

export function useLongPress(onLongPress: () => void, { thresholdMs = 500, moveTolerance = 10 }: Options = {}) {
  const timerRef = useRef<number | null>(null);
  const startRef = useRef<{ x: number; y: number } | null>(null);
  const firedRef = useRef(false);

  const clear = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    startRef.current = null;
  }, []);

  useEffect(() => clear, [clear]);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (e.pointerType === "mouse" && e.button !== 0) return;
      firedRef.current = false;
      startRef.current = { x: e.clientX, y: e.clientY };
      timerRef.current = window.setTimeout(() => {
        firedRef.current = true;
        timerRef.current = null;
        if (navigator.vibrate) navigator.vibrate(20);
        onLongPress();
      }, thresholdMs);
    },
    [onLongPress, thresholdMs],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!startRef.current) return;
      const dx = e.clientX - startRef.current.x;
      const dy = e.clientY - startRef.current.y;
      if (Math.hypot(dx, dy) > moveTolerance) clear();
    },
    [clear, moveTolerance],
  );

  const onPointerUp = useCallback(() => clear(), [clear]);
  const onPointerCancel = useCallback(() => clear(), [clear]);
  const onContextMenu = useCallback(
    (e: React.MouseEvent) => {
      if (firedRef.current) e.preventDefault();
    },
    [],
  );

  return { onPointerDown, onPointerMove, onPointerUp, onPointerCancel, onContextMenu };
}
